import config from 'config';
import { differenceInYears, endOfDay } from 'date-fns';
import {
  AnimalGender,
  AnimalSpecies,
  BloodType,
  DonorStatus,
  DonorType,
} from 'gql/types';
import { RoutineTestGender } from 'modules/donor/components/RoutineTestGenderSelect/enum';
import { getTranslation } from 'utils/getTranslation';
import * as yup from 'yup';

import {
  DEFAULT_DONOR_AGE_RANGE,
  getAllowedDonorAge,
} from '../utils/get-allowed-donor-age';
import { isAllowedToValidateDonorAge } from '../utils/get-status-rules';

const t = getTranslation('donor.ValidationErrors');

const NAME_REGEX = /^[a-zA-Z\s\-\']+$/;

export const petAgeSchema = yup
  .number()
  .integer(t('mustBeInt'))
  .typeError(t('mustBeNumber'))
  .required(t('required', { field: t('petAge') }))
  .min(1, t('olderThan', { min: 1 }));

export const petDateOfBirthSchema = yup
  .date()
  .typeError(t('mustBeValidDate'))
  .required(t('required', { field: t('petBirthDate') }))
  .transform((curr, orig) => (orig === '' ? null : curr))
  .test(
    'not-future',
    t('mustBeInPast', { field: t('petBirthDate') }),
    (value) => {
      if (!value) {
        return true;
      }

      const endOfToday = endOfDay(new Date());
      return value <= endOfToday;
    }
  )
  .test('min-age', function (this, value) {
    if (!value) {
      return true;
    }

    const parent = this?.parent ?? {};
    const status = parent.status;
    if (!isAllowedToValidateDonorAge(status)) {
      return true;
    }

    const species = parent.species;
    const ageRange = species
      ? getAllowedDonorAge(species)
      : DEFAULT_DONOR_AGE_RANGE;

    const age = differenceInYears(new Date(), new Date(value));
    return (
      age >= ageRange.min ||
      this.createError({
        message: t('donorMustBeAtLeastXYearOld', { years: ageRange.min }),
      })
    );
  });

export const petNameSchema = yup
  .string()
  .strict(true)
  .trim(t('mustBeTrimmed'))
  .typeError(t('mustBeString'))
  .required(t('required', { field: t('petName') }))
  .min(1, t('longerThan', { min: 1, field: t('petName') }))
  .max(30, t('shorterThan', { max: 30, field: t('petName') }))
  .matches(NAME_REGEX, t('onlyAlphabets')); //Alphabets & spaces;

export const petWeightSchema = yup
  .number()
  .typeError(t('mustBeNumber'))
  .positive(t('mustBePositive'))
  .required(t('required', { field: t('petWeight') }))
  .when('species', (species, schema) => {
    const isDog = species === AnimalSpecies.Dog;

    if (isDog) {
      return schema
        .min(
          config.DONOR_CRITERIA.MIN_DOG_WEIGHT_LBS,
          t('minWeight', {
            min: config.DONOR_CRITERIA.MIN_DOG_WEIGHT_LBS,
            type: 'pounds',
          })
        )
        .max(
          config.DONOR_CRITERIA.MAX_DOG_WEIGHT_LBS,
          t('maxWeight', {
            max: config.DONOR_CRITERIA.MAX_DOG_WEIGHT_LBS,
            type: 'pounds',
          })
        );
    }

    return schema
      .min(
        config.DONOR_CRITERIA.MIN_CAT_WEIGHT_LBS,
        t('minWeight', {
          min: config.DONOR_CRITERIA.MIN_CAT_WEIGHT_LBS,
          type: 'pounds',
        })
      )
      .max(
        config.DONOR_CRITERIA.MAX_CAT_WEIGHT_LBS,
        t('maxWeight', {
          max: config.DONOR_CRITERIA.MAX_CAT_WEIGHT_LBS,
          type: 'pounds',
        })
      );
  })
  .transform((value, originalValue) => {
    return String(originalValue).trim() === '' ? undefined : value;
  });

export const petWeightSchemaInKg = yup
  .number()
  .typeError(t('mustBeNumber'))
  .required(t('required', { field: t('petWeight') }))
  .when('species', (species, schema) => {
    const isDog = species === AnimalSpecies.Dog;

    if (isDog) {
      return schema
        .min(
          config.DONOR_CRITERIA.MIN_DOG_WEIGHT_KG,
          t('minWeight', {
            min: config.DONOR_CRITERIA.MIN_DOG_WEIGHT_KG,
            type: 'kilograms',
          })
        )
        .max(
          config.DONOR_CRITERIA.MAX_DOG_WEIGHT_KG,
          t('maxWeight', {
            max: config.DONOR_CRITERIA.MAX_DOG_WEIGHT_KG,
            type: 'kilograms',
          })
        );
    }

    return schema
      .min(
        config.DONOR_CRITERIA.MIN_CAT_WEIGHT_KG,
        t('minWeight', {
          min: config.DONOR_CRITERIA.MIN_CAT_WEIGHT_KG,
          type: 'kilograms',
        })
      )
      .max(
        config.DONOR_CRITERIA.MAX_CAT_WEIGHT_KG,
        t('maxWeight', {
          max: config.DONOR_CRITERIA.MAX_CAT_WEIGHT_KG,
          type: 'kilograms',
        })
      );
  })
  .transform((value, originalValue) => {
    return originalValue === '' || originalValue === null ? undefined : value;
  });

export const petApprovedDonationMLSchema = yup
  .number()
  .typeError(t('mustBeNumber'));

export const petGenderSchema = yup
  .mixed<AnimalGender>()
  .oneOf(Object.values(AnimalGender))
  .nullable()
  .required(t('required', { field: t('petGender') }));

export const routineTestGenderSchema = yup
  .mixed<RoutineTestGender>()
  .oneOf(Object.values(RoutineTestGender))
  .required(t('required', { field: t('petGender') }));

export const petSpeciesSchema = yup
  .mixed<AnimalSpecies>()
  .oneOf(Object.values(AnimalSpecies))
  .nullable()
  .required(t('required', { field: t('species') }));

export const bloodTypeSchema = yup
  .mixed<BloodType>()
  .oneOf(Object.values(BloodType));

export const petMarksSchema = yup
  .string()
  .max(255, t('shorterThan', { max: 255, field: t('distinguishableMarks') }));

export const donorTypeSchema = yup
  .mixed<DonorType>()
  .oneOf(Object.values(DonorType));

export const donorStatusSchema = yup
  .mixed<DonorStatus>()
  .oneOf(Object.values(DonorStatus));

export const teamTitleSchema = yup
  .string()
  .typeError(t('mustBeString'))
  .min(3)
  .matches(/^[A-Za-z0-9\s]+$/g, t('onlyValidSymbols'))
  .test({
    test: (value) => value?.trim() === value,
    message: t('mustBeTrimmed'),
  })
  .max(50);

export const groupTitleSchema = yup
  .string()
  .typeError(t('mustBeString'))
  .min(3)
  .max(50)
  .matches(/^\S.*\S$|^\S$/, t('noSpacesAtEdgesMessage'));

export const breedSchema = yup
  .string()
  .nullable()
  .transform((value) => (value === '' ? null : value))
  .typeError(t('mustBeString'))
  .min(2, t('longerThan', { min: 2, field: t('breed') }))
  .max(50, t('shorterThan', { max: 50, field: t('breed') }))
  .matches(/^[aA-zZ ]+$/, t('onlyAlphabets'));

export const colorSchema = yup
  .string()
  .nullable()
  .transform((value) => (value === '' ? null : value))
  .typeError(t('mustBeString'))
  .min(2, t('longerThan', { min: 2, field: t('color') }))
  .max(50, t('shorterThan', { max: 50, field: t('color') }))
  .matches(/^[aA-zZ ]+$/, t('onlyAlphabets'));

export const donorHistoryDateSchema = yup
  .date()
  .nullable()
  .test(
    'not-in-future',
    ({ path }) => {
      const fieldName = path.split('.').pop() || path;
      return t('dateCannotBeInFuture', { field: t(fieldName) });
    },
    (value) => {
      if (!value) {
        return true;
      }

      return value.getTime() <= new Date().setHours(23, 59, 59, 999);
    }
  );
